import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Send, MessageCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { conversationsAPI, messagesAPI } from '../api';
import { toast } from 'sonner';

const Messages = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadConvs = async () => {
    try {
      const res = await conversationsAPI.list();
      setConversations(res.data || []);
      const target = searchParams.get('booking');
      if (target && !selected) {
        const match = (res.data || []).find(c => c.booking_id === target);
        if (match) setSelected(match);
      }
    } catch (e) {
      toast.error('Error cargando conversaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    loadConvs();
    const t = setInterval(loadConvs, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMessages = async (conv) => {
    if (!conv) return;
    try {
      const res = await messagesAPI.getBookingMessages(conv.booking_id);
      setMessages(res.data || []);
      await conversationsAPI.markRead(conv.booking_id);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (selected) loadMessages(selected); }, [selected]);

  const send = async () => {
    if (!newMessage.trim() || !selected || sending) return;
    setSending(true);
    try {
      await messagesAPI.create({
        booking_id: selected.booking_id,
        receiver_id: selected.other_user.id,
        message: newMessage.trim(),
      });
      setNewMessage('');
      loadMessages(selected);
      loadConvs();
    } catch (e) {
      toast.error('Error enviando mensaje');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Mensajes</h1>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="md:col-span-1" data-testid="conversations-list">
            <CardHeader><CardTitle>Conversaciones ({conversations.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {conversations.length > 0 ? (
                <div className="divide-y">
                  {conversations.map((c) => (
                    <button
                      key={c.booking_id}
                      onClick={() => setSelected(c)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition ${selected?.booking_id === c.booking_id ? 'bg-blue-50' : ''}`}
                      data-testid={`conv-${c.booking_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar><AvatarFallback>{c.other_user.name[0]}</AvatarFallback></Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className="font-semibold truncate text-sm">{c.other_user.name}</p>
                            {c.unread > 0 && <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 ml-1">{c.unread}</span>}
                          </div>
                          <p className="text-xs text-gray-600 truncate">{c.car_name}</p>
                          <p className="text-xs text-gray-500 truncate mt-1">{c.last_message}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-sm">No tienes conversaciones aún</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="md:col-span-2" data-testid="messages-pane">
            {selected ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-center gap-3">
                    <Avatar><AvatarFallback>{selected.other_user.name[0]}</AvatarFallback></Avatar>
                    <div>
                      <CardTitle className="text-lg">{selected.other_user.name}</CardTitle>
                      <p className="text-xs text-gray-600">{selected.car_name}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-96 overflow-y-auto p-4 space-y-3 bg-gray-50">
                    {messages.length > 0 ? messages.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border rounded-bl-sm'}`}>
                            <p>{m.message}</p>
                            <p className={`text-[10px] mt-1 ${mine ? 'text-blue-100' : 'text-gray-400'}`}>
                              {new Date(m.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="text-center py-12 text-gray-500 text-sm">Inicia la conversación 👋</div>
                    )}
                    <div ref={bottomRef} />
                  </div>

                  <div className="border-t p-3 bg-white">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Escribe un mensaje..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button onClick={send} disabled={sending || !newMessage.trim()} className="bg-blue-600" data-testid="send-message-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full min-h-96">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">Selecciona una conversación para empezar</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Messages;
